import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { subjectForListMatch } from '../utils/folderDisplay';

function dateInputValue(value) {
  const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

// Course dropdowns come from student lists ("6A+6B · URDU"). A saved subject
// like "URDU Obj" still selects that course, and the Obj/Sub words move into
// Only for more Papers when that field is empty.
function pickCourse(options, className, subject, paperType) {
  const norm = (value) => String(value || '').trim().toLowerCase();
  const exact = options.find((option) =>
    norm(option.classVal) === norm(className) && norm(option.subject) === norm(subject)
  );
  if (exact) return { course: exact, paperType: String(paperType || '').trim() };
  const baseSubject = subjectForListMatch(subject, paperType);
  const base = options.find((option) =>
    norm(option.classVal) === norm(className) && norm(option.subject) === norm(baseSubject)
  );
  let nextPaper = String(paperType || '').trim();
  const raw = String(subject || '').trim();
  if (!nextPaper && base && norm(raw) !== norm(baseSubject) && norm(raw).startsWith(norm(baseSubject))) {
    nextPaper = raw.slice(baseSubject.length).trim();
  }
  if (base) return { course: base, paperType: nextPaper };
  if (!className || !subject) return { course: null, paperType: nextPaper };
  return {
    course: {
      value: `${className}\0${subject}`,
      label: `${className} \u00b7 ${subject}`,
      classVal: className,
      subject
    },
    paperType: nextPaper
  };
}

export default function SaveRubricModal({ isOpen, onClose, onSave, currentUser, initialRubric }) {
  const [formData, setFormData] = useState({
    classSubjectId: null,
    class: '',
    subject: '',
    // Optional human-friendly label shown on every folder card so two folders
    // for the same class/subject can be told apart (e.g. "Chapter 3 Quiz").
    assignmentName: '',
    paperType: '',
    validUntil: '',
    // 'subjective' = handwritten answer-sheet rubric, 'mcq' = OMR bubble sheet
    // rubric. Defaults to subjective because the historical flow is
    // subjective-only — users opting into MCQs have to switch explicitly.
    type: 'subjective',
    // When an examiner / admin creates on behalf of a teacher. Empty
    // string = "create as myself".
    createdByUsername: ''
  });
  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  // Student lists (each carries class/subject + comma-separated teacher_names)
  // used to narrow the "Create on behalf of" dropdown to the teacher(s)
  // actually assigned to the selected course.
  const [studentLists, setStudentLists] = useState([]);
  const [coursesReady, setCoursesReady] = useState(false);
  const userEditedRef = useRef(false);

  const API_BASE_URL = window.API_BASE_URL;
  // Only elevated roles see the "Create on behalf of teacher" dropdown.
  const canDelegate = !!currentUser && ['examiner', 'administrator', 'management'].includes(currentUser.role);

  // Fetch courses from student lists when modal opens (section ignored;
  // distinct class + subject). Courses tab catalog is no longer used.
  useEffect(() => {
    if (!isOpen) {
      setCoursesReady(false);
      userEditedRef.current = false;
      return undefined;
    }
    if (!initialRubric) {
      setFormData({ classSubjectId: null, class: '', subject: '', assignmentName: '', paperType: '', validUntil: '', type: 'subjective', createdByUsername: '' });
      setSelectedCourse(null);
      setSelectedTeacher(null);
    }
    let cancelled = false;
    const fetchCourses = async () => {
      setLoadingCourses(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/students/lists`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed to fetch courses');
        const data = await response.json();
        const lists = Array.isArray(data) ? data : [];
        const seen = new Set();
        const options = [];
        for (const item of lists) {
          const classVal = (item.class || '').trim();
          const subject = (item.subject || '').trim();
          if (!classVal || !subject) continue;
          const key = `${classVal}\0${subject}`;
          if (seen.has(key)) continue;
          seen.add(key);
          options.push({
            value: key,
            label: `${classVal} \u00b7 ${subject}`,
            classVal,
            subject
          });
        }
        options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        if (!cancelled) setCourseOptions(options);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        if (!cancelled) {
          setLoadingCourses(false);
          setCoursesReady(true);
        }
      }
    };
    fetchCourses();
    return () => { cancelled = true; };
  }, [isOpen, initialRubric, API_BASE_URL]);

  // Fill the save form from the rubric being edited, once the course list
  // has arrived. A later edit by the user is left alone.
  useEffect(() => {
    if (!isOpen || !initialRubric || !coursesReady || userEditedRef.current) return;
    const picked = pickCourse(courseOptions, initialRubric.class, initialRubric.subject, initialRubric.paper_type || initialRubric.paperType);
    const course = picked.course;
    setSelectedCourse(course);
    setFormData({
      classSubjectId: null,
      class: course ? course.classVal : (initialRubric.class || ''),
      subject: course ? course.subject : (initialRubric.subject || ''),
      assignmentName: initialRubric.assignment_name || initialRubric.assignmentName || '',
      paperType: picked.paperType,
      validUntil: dateInputValue(initialRubric.test_date || initialRubric.validUntil),
      type: String(initialRubric.type || '').toLowerCase() === 'mcq' ? 'mcq' : 'subjective',
      createdByUsername: initialRubric.created_by || ''
    });
  }, [isOpen, initialRubric, coursesReady, courseOptions]);

  useEffect(() => {
    if (!isOpen || !initialRubric || !canDelegate || userEditedRef.current) return;
    const owner = teacherOptions.find((teacher) => teacher.value === initialRubric.created_by);
    if (owner) setSelectedTeacher(owner);
  }, [isOpen, initialRubric, canDelegate, teacherOptions]);

  // Fetch the list of teachers — only for elevated roles, only when the
  // modal is open. Cheap one-shot fetch; the list is short.
  useEffect(() => {
    if (!isOpen || !canDelegate) return;
    const fetchTeachers = async () => {
      setLoadingTeachers(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/auth/teachers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed to fetch teachers');
        const data = await response.json();
        const options = (data.teachers || []).map(t => ({
          value: t.username,
          label: t.full_name ? `${t.full_name} (${t.username})` : t.username,
          full_name: t.full_name,
          email: t.email
        }));
        setTeacherOptions(options);
      } catch (error) {
        console.error('Error fetching teachers:', error);
      } finally {
        setLoadingTeachers(false);
      }
    };
    fetchTeachers();
  }, [isOpen, canDelegate, API_BASE_URL]);

  // Fetch the student lists so the teacher dropdown can be scoped to the
  // selected course. Same gating as the teacher fetch — only elevated roles
  // see (and need) the delegation dropdown. Non-fatal on failure: the dropdown
  // simply falls back to listing every teacher.
  useEffect(() => {
    if (!isOpen || !canDelegate) return;
    const fetchStudentLists = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/students/lists`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setStudentLists(Array.isArray(data) ? data : []);
      } catch (_) { /* non-fatal — falls back to all teachers */ }
    };
    fetchStudentLists();
  }, [isOpen, canDelegate, API_BASE_URL]);

  const markEdited = () => { userEditedRef.current = true; };

  const handleTeacherChange = (selected) => {
    markEdited();
    setSelectedTeacher(selected);
    setFormData(prev => ({ ...prev, createdByUsername: selected ? selected.value : '' }));
  };

  const handleCourseChange = (selected) => {
    markEdited();
    setSelectedCourse(selected);
    // A different course can have a different set of teachers — clear any
    // teacher picked for the previous course so we never submit a teacher who
    // isn't assigned to the newly-selected one.
    setSelectedTeacher(null);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        // Courses come from student lists now; rubrics store class/subject
        // strings only (backend does not require class_subject_id).
        classSubjectId: null,
        class: selected.classVal,
        subject: selected.subject,
        createdByUsername: ''
      }));
    } else {
      setFormData(prev => ({ ...prev, classSubjectId: null, class: '', subject: '', createdByUsername: '' }));
    }
  };

  const resetForm = () => {
    setFormData({ classSubjectId: null, class: '', subject: '', assignmentName: '', paperType: '', validUntil: '', type: 'subjective', createdByUsername: '' });
    setSelectedCourse(null);
    setSelectedTeacher(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.class || !formData.subject) return;
    // Assignment name is OPTIONAL — no guard on it.
    // A thrown error leaves the form open so the teacher can correct it.
    try {
      await onSave(formData);
      resetForm();
    } catch (_) { /* caller reports the failure */ }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  // Only Course is required to save — Assignment Name is optional.
  const canSave = !!selectedCourse;

  // Narrow the "Create on behalf of" list to the teacher(s) assigned to the
  // selected course. The assignment comes from the student lists' "Teachers:"
  // field (comma-separated full names), matched to teacher accounts by
  // full_name. Class is matched exactly (normalized); subject is matched on
  // its base name so a course "CHEMISTRY" still lines up with a list subject
  // like "chemistry-a". When several lists / teachers match, all are shown.
  // Falls back to the full teacher list when no course is picked or no
  // assigned teacher can be resolved, so the dropdown is never unexpectedly
  // empty.
  const normName = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const normClass = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, '');
  const normSubject = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, '').replace(/-[a-z0-9]+$/, '');

  const courseTeacherNames = new Set();
  if (selectedCourse) {
    const cls = normClass(selectedCourse.classVal);
    const sub = normSubject(selectedCourse.subject);
    for (const l of studentLists) {
      if (normClass(l.class) !== cls || normSubject(l.subject) !== sub) continue;
      (l.teacher_names || '').split(',').forEach((n) => {
        const nn = normName(n);
        if (nn) courseTeacherNames.add(nn);
      });
    }
  }
  const courseTeacherOptions = teacherOptions.filter(o => courseTeacherNames.has(normName(o.full_name)));
  const teacherListScoped = !!selectedCourse && courseTeacherOptions.length > 0;
  const visibleTeacherOptions = teacherListScoped ? courseTeacherOptions : teacherOptions;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10050,
      }}
      onClick={handleCancel}
    >
      <div
        style={{
          background: 'var(--panel-bg, white)',
          borderRadius: 16,
          padding: 32,
          maxWidth: 500,
          width: '90%',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, marginBottom: 24, fontSize: 24, fontWeight: 600 }}>
          {initialRubric ? 'Edit Rubric' : 'Save Rubric'}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Course Selection */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              Course *
            </label>
            <Select
              value={selectedCourse}
              onChange={handleCourseChange}
              options={courseOptions}
              isLoading={loadingCourses}
              isSearchable
              isClearable
              placeholder="Search or select a course..."
              noOptionsMessage={() =>
                currentUser?.role === 'teacher'
                  ? 'No courses found. Ask an examiner to import a student list that includes you as teacher.'
                  : 'No courses found. Import a student list in the Students tab first.'
              }
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: 8,
                  borderColor: 'var(--border, #ddd)',
                  fontSize: 14,
                  minHeight: 42,
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 10000,
                }),
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="paperType"
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              Only for more Papers
            </label>
            <input
              type="text"
              id="paperType"
              name="paperType"
              value={formData.paperType}
              onChange={(e) => { markEdited(); setFormData(prev => ({ ...prev, paperType: e.target.value })); }}
              placeholder="Optional (e.g. Sub/Obj) — Leave empty if single paper"
              maxLength={255}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border, #ddd)',
                borderRadius: 8,
                fontSize: 14,
                background: 'var(--panel-bg, white)',
                color: 'var(--text-primary)',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Assignment Name — optional label that rides along on the folder
              everywhere it's shown (Scans / Sort / MCQs cards). */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="assignmentName"
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              Assignment Name (Optional)
            </label>
            <input
              type="text"
              id="assignmentName"
              name="assignmentName"
              value={formData.assignmentName}
              onChange={(e) => { markEdited(); setFormData(prev => ({ ...prev, assignmentName: e.target.value })); }}
              placeholder="Copy assignment name from EduPage by triple clicking it"
              maxLength={255}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border, #ddd)',
                borderRadius: 8,
                fontSize: 14,
                background: 'var(--panel-bg, white)',
                color: 'var(--text-primary)',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Test type — distinguishes a Subjective-paper rubric from an
              MCQs/OMR rubric so two rubrics with the same class+subject+date
              can coexist when they cover different test types. */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="rubric-type"
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              Test Type *
            </label>
            <select
              id="rubric-type"
              value={formData.type}
              onChange={(e) => { markEdited(); setFormData(prev => ({ ...prev, type: e.target.value })); }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border, #ddd)',
                borderRadius: 8,
                fontSize: 14,
                background: 'var(--panel-bg, white)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <option value="subjective">Subjective (handwritten answer sheets)</option>
              <option value="mcq">MCQs (OMR bubble sheets)</option>
            </select>
          </div>

          {/* Create on behalf of teacher — only visible to elevated roles.
              When set, the backend records the rubric under the teacher's
              username so it appears in the teacher's "my rubrics" list and
              the "Created by: ..." line shows the teacher's name. */}
          {canDelegate && (
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 500,
                  fontSize: 14,
                }}
              >
                Create on behalf of (Optional)
              </label>
              <Select
                value={selectedTeacher}
                onChange={handleTeacherChange}
                options={visibleTeacherOptions}
                isLoading={loadingTeachers}
                isSearchable
                isClearable
                placeholder={teacherListScoped
                  ? 'Pick a teacher assigned to this course...'
                  : 'Leave blank to create as yourself, or pick a teacher...'}
                noOptionsMessage={() => teacherListScoped
                  ? 'No teacher assigned to this course.'
                  : 'No active teachers found.'}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 8,
                    borderColor: 'var(--border, #ddd)',
                    fontSize: 14,
                    minHeight: 42,
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 10000,
                  }),
                }}
              />
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
                {selectedTeacher
                  ? <>This rubric will be saved as created by <strong>{selectedTeacher.label}</strong> and visible to that teacher.</>
                  : teacherListScoped
                    ? <>Showing teacher(s) assigned to <strong>{selectedCourse.label}</strong>. Leave blank to save as yourself.</>
                    : 'Leave blank to save as yourself.'}
              </div>
            </div>
          )}

          {/* Valid Until Date */}
          <div style={{ marginBottom: 24 }}>
            <label
              htmlFor="validUntil"
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              Valid Until (Optional)
            </label>
            <input
              type="date"
              id="validUntil"
              name="validUntil"
              value={formData.validUntil}
              onChange={(e) => { markEdited(); setFormData(prev => ({ ...prev, validUntil: e.target.value })); }}
              onClick={(e) => {
                // By default browsers only open the date picker when the small
                // calendar icon is clicked. Force it open on any click inside
                // the input so users can tap anywhere in the field.
                if (typeof e.currentTarget.showPicker === 'function') {
                  try { e.currentTarget.showPicker(); } catch {}
                }
              }}
              onFocus={(e) => {
                if (typeof e.currentTarget.showPicker === 'function') {
                  try { e.currentTarget.showPicker(); } catch {}
                }
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border, #ddd)',
                borderRadius: 8,
                fontSize: 14,
                cursor: 'pointer',
              }}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="submit"
              disabled={!canSave}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: canSave ? '#4caf50' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >
              {initialRubric ? 'Save changes' : 'Save Rubric'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: '#999',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
